import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, useForm } from "react-hook-form";
import { ReactComponent as SearchIcon } from "assets/images/icons/search.svg";

import { messages } from "./messages";
import { Button, Card, Checkbox, Input } from "@letsbit/milkylb";
import {
  ACCOUNT_PROVIDERS,
  GetMerkActivitiesParams,
  MERK_ACTIVITY_FILTER_BY_DATE,
  MERK_ACTIVITY_TYPE,
} from "@letsbit/malcolmlb";
import { BackButton } from "components/BackButton";
import dayjs from "dayjs";
import { ModalSelector } from "components/ModalSelector";
import { useGetMerkAssetsBasic } from "hooks/merk/useGetMerkAssetsBasic";
import { Pill } from "components/Pill";
import { filterAssets } from "utils/filters";
import { BymaActivitiesAdvancedSearchProps } from "./types";
import DatePicker from "components/DatePicker/DatePicker";
import { useNavigate } from "react-router-dom";

export const BymaActivitiesAdvancedSearch: React.FC<
  BymaActivitiesAdvancedSearchProps
> = ({ filter, setFilter, onClose, memberUid }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { handleSubmit, control, setValue, getValues } =
    useForm<GetMerkActivitiesParams>({
      defaultValues: {
        ...filter,
      },
    });

  const { data: assets } = useGetMerkAssetsBasic({
    provider: ACCOUNT_PROVIDERS.ESCO,
  });
  const assetsMapped = assets?.map((asset) => ({
    ...asset,
    id: asset.external_id,
  }));
  useEffect(() => {
    setValue("assets", []);
  }, [setValue]);

  return (
    <form
      className="flex h-full flex-col justify-between"
      onSubmit={handleSubmit((data) => {
        setFilter(data);
        onClose();
      })}
    >
      <div>
        <div className="mb-8 flex items-center justify-between border-b border-[--milkylb-color-secondary-100] pb-8">
          <div>
            <BackButton className="mb-4" onClick={onClose} />
            <h1 className="font-['Tomato_Grotesk'] text-4xl font-bold">
              <FormattedMessage {...messages.title} />
            </h1>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() =>
                navigate(
                  memberUid
                    ? `/logged/members/${memberUid}/local-market/history`
                    : "/logged/byma/activities",
                  { state: getValues() },
                )
              }
            >
              <SearchIcon className="mr-2 fill-white" />
              <FormattedMessage {...messages.search} />
            </Button>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="mb-2 flex gap-8">
            <div>
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.movementType} />
              </h3>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="flex flex-row gap-x-6">
                    <Pill
                      selected={value === MERK_ACTIVITY_TYPE.RECEIPT}
                      onClick={() => onChange(MERK_ACTIVITY_TYPE.RECEIPT)}
                    >
                      <FormattedMessage {...messages.receiptPill} />
                    </Pill>
                    <Pill
                      selected={value === MERK_ACTIVITY_TYPE.COUPON}
                      onClick={() => onChange(MERK_ACTIVITY_TYPE.COUPON)}
                    >
                      <FormattedMessage {...messages.couponPill} />
                    </Pill>
                  </div>
                )}
                name="activity_types"
              />
            </div>
          </div>
          <div className="grid w-[420px] grid-cols-2 gap-2">
            <div className="flex flex-col">
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.since} />
              </h3>
              <Controller
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div className="mb-8 grid-cols-2 gap-2">
                    <DatePicker
                      floatingLabel={intl.formatMessage(messages.chooseDate)}
                      value={value ? dayjs(value) : null}
                      onChange={(event) => {
                        onChange(event?.toISOString());
                      }}
                      error={error}
                    />
                  </div>
                )}
                name="start_date"
              />
            </div>
            <div className="flex flex-col">
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.until} />
              </h3>
              <Controller
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div className="mb-8 grid-cols-2 gap-2">
                    <DatePicker
                      floatingLabel={intl.formatMessage(messages.chooseDate)}
                      value={value ? dayjs(value) : null}
                      onChange={(event) => {
                        onChange(event?.toISOString());
                      }}
                      error={error}
                    />
                  </div>
                )}
                name="end_date"
              />
            </div>
          </div>
          <div className="mb-2 flex gap-8">
            <div>
              <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
                <FormattedMessage {...messages.byDateType} />
              </h3>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="flex flex-row gap-x-6">
                    <Pill
                      selected={
                        value === MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT
                      }
                      onClick={() =>
                        onChange(MERK_ACTIVITY_FILTER_BY_DATE.AGREEMENT)
                      }
                    >
                      <FormattedMessage {...messages.agreementPill} />
                    </Pill>
                    <Pill
                      selected={
                        value === MERK_ACTIVITY_FILTER_BY_DATE.LIQUIDATION
                      }
                      onClick={() =>
                        onChange(MERK_ACTIVITY_FILTER_BY_DATE.LIQUIDATION)
                      }
                    >
                      <FormattedMessage {...messages.liquidationPill} />
                    </Pill>
                  </div>
                )}
                name="by_date"
              />
            </div>
          </div>
          <div>
            <h3 className="mb-2 font-['Source_Sans_Pro'] text-lg font-normal text-[--milkylb-color-neutral-600]">
              <FormattedMessage {...messages.selectCurrency} />
            </h3>
            <Controller
              key="currencies-selector"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className="mb-8 w-[420px] gap-2">
                  <ModalSelector
                    title={
                      <h3 className="mb-8 text-center text-xl font-bold">
                        <FormattedMessage {...messages.sendTitle} />
                      </h3>
                    }
                    filter={filterAssets}
                    optionComponent={(asset) => (
                      <Card
                        className="shrink-0 cursor-pointer"
                        key={asset.element.id}
                        title={asset.element.symbol.toUpperCase() || ""}
                        subtitle={asset.element.name || ""}
                      >
                        <Checkbox
                          checked={
                            !!value?.find((a) => a === asset.element.symbol)
                          }
                        />
                      </Card>
                    )}
                    options={assetsMapped}
                    closeOnSelect={false}
                    onSelectOption={(c) => {
                      if (value?.find((a) => a === c.symbol)) {
                        onChange(value?.filter((a) => a !== c.symbol));
                      } else {
                        if (value) {
                          onChange([...value, c.symbol]);
                        } else {
                          onChange([c.symbol]);
                        }
                      }
                    }}
                  >
                    {({ onClick }) => {
                      return (
                        <Input
                          onClick={onClick}
                          value={
                            value &&
                            value
                              .map((symbol, index, array) => {
                                const ticker_id = assetsMapped?.find(
                                  (c) => c.symbol === symbol,
                                )?.name;
                                if (index === array.length - 1) {
                                  return ticker_id?.toUpperCase();
                                } else if (index === array.length - 2) {
                                  return `${ticker_id?.toUpperCase()} y`;
                                } else {
                                  return `${ticker_id?.toUpperCase()},`;
                                }
                              })
                              .join(" ")
                          }
                          placeholder={intl.formatMessage(messages.sendTitle)}
                          labelPlacement="floating"
                        />
                      );
                    }}
                  </ModalSelector>
                </div>
              )}
              name="assets"
            />
          </div>
        </div>
      </div>
    </form>
  );
};
